import { Box, Flex, VStack } from '@chakra-ui/react'
import { LuCircleCheck, LuPrinter, LuX } from 'react-icons/lu'
import { Text, useT } from '@minetrace/ui'
import { brand } from '../../theme'
import type { GenerateSamplesResponse } from '../../api/models/recepcion.models'

interface Props {
  open: boolean
  loading: boolean
  error: string | null
  data: GenerateSamplesResponse | null
  onClose: () => void
  onPrinted: () => Promise<void>
}

const PURPOSE_LABELS: Record<string, string> = {
  PlantLab: 'Laboratorio de Planta',
  Supplier: 'Proveedor',
  Tiebreaker: 'Dirimencia',
  Extra: 'Adicional',
}

// Imprime la vista de etiquetas en una nueva ventana. Cada muestra ocupa una
// "etiqueta" CSS de tamaño fijo; el navegador maneja salto de página.
const printSamples = (data: GenerateSamplesResponse, onAfter: () => void) => {
  const w = window.open('', '_blank', 'noopener,noreferrer')
  if (!w) return

  const rows = data.samples
    .map(
      (s) => `
      <div class="label">
        <div class="title">Lote Muestreado</div>
        <div class="subtitle">Entregue esta muestra a ${PURPOSE_LABELS[s.purpose] ?? s.purpose}</div>
        <div class="code">${s.code}</div>
      </div>
    `,
    )
    .join('')

  w.document.write(`<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Etiquetas de muestreo - ${data.batchCode}</title>
<style>
  @page { size: A6 landscape; margin: 6mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Segoe UI, Roboto, sans-serif; margin: 0; padding: 0; }
  .label {
    width: 100%; min-height: 80mm; page-break-after: always;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    border: 2px dashed #c4c4c4; padding: 8mm; text-align: center;
  }
  .title { font-size: 14pt; font-weight: 700; color: #b4532a; margin-bottom: 4mm; }
  .subtitle { font-size: 10pt; color: #555; margin-bottom: 6mm; }
  .code {
    font-family: 'Courier New', monospace; font-size: 28pt; font-weight: 800;
    letter-spacing: 0.04em; color: #111; border: 1px solid #111; padding: 4mm 6mm;
  }
</style>
</head>
<body>${rows}<script>window.onload = () => { window.print(); }</script></body>
</html>`)
  w.document.close()
  // Best-effort: marcar muestras como impresas
  void onAfter()
}

export function GenerateSamplesModal({
  open,
  loading,
  error,
  data,
  onClose,
  onPrinted,
}: Props) {
  const { t: _t } = useT()
  void _t

  if (!open) return null

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="rgba(0,0,0,0.5)"
      zIndex={1000}
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={4}
      onClick={onClose}
    >
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="2xl"
        maxW="520px"
        w="100%"
        p={6}
        onClick={(e) => e.stopPropagation()}
      >
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Text fontSize="lg" fontWeight="700" color={brand.primary}>
            Muestreo del lote
          </Text>
          <Box
            as="button"
            cursor="pointer"
            color="#857b71"
            _hover={{ color: '#111' }}
            onClick={onClose}
          >
            <LuX size={20} />
          </Box>
        </Flex>

        {loading && (
          <Text fontSize="sm" color="#857b71" textAlign="center" py={8}>
            Generando codigos de muestreo...
          </Text>
        )}

        {error && !loading && (
          <Box bg="#fae1db" color="#9a3020" borderRadius="md" p={3} fontSize="sm">
            {error}
          </Box>
        )}

        {!loading && !error && data && (
          <VStack gap={4} alignItems="stretch">
            <Flex
              direction="column"
              alignItems="center"
              gap={2}
              py={4}
              bg="#eef7f6"
              borderRadius="lg"
              border="1px solid"
              borderColor="#86EFAC"
            >
              <Box color="#2a8a87">
                <LuCircleCheck size={32} />
              </Box>
              <Text fontWeight="700" color="#1d6664" fontSize="md">
                Lote Muestreado Exitosamente
              </Text>
              <Text fontSize="xs" color="#1d6664">
                Entregue cada muestra física al destinatario indicado
              </Text>
              <Box
                mt={2}
                px={4}
                py={2}
                border="1px solid #111"
                borderRadius="md"
                fontFamily="'Courier New', monospace"
                fontSize="xl"
                fontWeight="800"
                letterSpacing="0.04em"
              >
                MUE-{data.batchNumber}
              </Box>
            </Flex>

            <VStack gap={1} alignItems="stretch">
              {data.samples.map((s) => (
                <Flex
                  key={s.id}
                  justifyContent="space-between"
                  alignItems="center"
                  px={3}
                  py={2}
                  bg="#faf6f0"
                  borderRadius="md"
                >
                  <Text
                    fontFamily="'Courier New', monospace"
                    fontSize="sm"
                    fontWeight="700"
                  >
                    {s.code}
                  </Text>
                  <Text fontSize="xs" color="#857b71">
                    {PURPOSE_LABELS[s.purpose] ?? s.purpose}
                  </Text>
                </Flex>
              ))}
            </VStack>

            <Flex justifyContent="flex-end" gap={2}>
              <Box
                as="button"
                px={4}
                py={2}
                bg="white"
                color={brand.primary}
                border="1px solid"
                borderColor={brand.primary}
                borderRadius="md"
                fontSize="sm"
                fontWeight="600"
                cursor="pointer"
                _hover={{ bg: '#faf6f0' }}
                onClick={onClose}
              >
                Cerrar
              </Box>
              <Box
                as="button"
                display="flex"
                alignItems="center"
                gap={2}
                px={4}
                py={2}
                bg={brand.primary}
                color="white"
                borderRadius="md"
                fontSize="sm"
                fontWeight="600"
                cursor="pointer"
                _hover={{ opacity: 0.9 }}
                onClick={() => printSamples(data, onPrinted)}
              >
                <LuPrinter size={16} />
                Imprimir etiquetas
              </Box>
            </Flex>
          </VStack>
        )}
      </Box>
    </Box>
  )
}
